/**
 * One guest-session operation at a time, across tabs (contract: "a Web Locks
 * lock stops two tabs creating two guests"). Guest refresh tokens rotate and
 * are single-use, so two tabs refreshing with the same cookie would read as
 * replay and revoke the family — the same lock serialises refreshes too.
 *
 * Fallback without `navigator.locks` (old Safari < 15.4, jsdom): a per-tab
 * promise chain. That still stops this tab racing itself. Two SEPARATE such
 * tabs can still race each other: for a create race the worst case is an
 * orphaned guest row the retention cron removes, but for a refresh race the
 * worst case is the server reading the second tab's presentation of the same
 * rotating token as replay and revoking the whole family — ending the guest
 * session for both tabs, not just leaving a stray row.
 *
 * Non-reentrant: `attemptGuestRefresh` acquires it — never call that (or
 * this) from inside `work`.
 */
export const GUEST_SESSION_LOCK = "garden-guest-session";

let fallbackTail: Promise<unknown> = Promise.resolve();

export async function withGuestSessionLock<T>(work: () => Promise<T>): Promise<T> {
  const locks =
    typeof navigator === "undefined"
      ? undefined
      : (navigator as unknown as { locks?: LockManager }).locks;
  if (locks !== undefined && typeof locks.request === "function") {
    return await locks.request(GUEST_SESSION_LOCK, () => work());
  }
  const run = fallbackTail.then(work, work);
  fallbackTail = run.then(
    () => undefined,
    () => undefined,
  );
  return await run;
}
